import styles from "./ReasonToBuy.module.scss";
import { useRef, useState, useEffect, KeyboardEvent } from "react";
import { UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { ChevronLeftOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronLeftOutlined";
import { ChevronRightOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronRightOutlined";
import classNames from "classnames/bind";
import { Card } from "./ReasonToBuy";
import {
  getCardsPerPage,
  getClosestCardIndex,
  MatchMediaState,
  shouldShowChevronNavigation,
} from "./utils/utils";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { useR2BInView } from "./useR2BInView";

const cx = classNames.bind(styles);

const BREAKPOINTS = {
  smallMobileMax: 600,
  largeMobileMin: 601,
  largeMobileMax: 767,
  tabletMin: 768,
  tabletMax: 1023,
  smallDesktopMin: 1024,
  smallDesktopMax: 1279,
  largeDesktopMin: 1280,
  largeDesktopMax: 1439,
  xLargeDesktopMin: 1440,
} as const;

const rangeQuery = (min: number, max: number) =>
  `(min-width: ${min}px) and (max-width: ${max}px)`;
const minQuery = (min: number) => `(min-width: ${min}px)`;

// 0-599px: single card
const SMALL_MOBILE_SCROLL_MEDIA_QUERY = `(max-width: ${BREAKPOINTS.smallMobileMax}px)`;
// 600-767px: large mobile, 1 card + peek
const LARGE_MOBILE_SCROLL_MEDIA_QUERY = rangeQuery(
  BREAKPOINTS.largeMobileMin,
  BREAKPOINTS.largeMobileMax,
);
// 768-1023px: small tablet, 2 cards + peek
const VERY_SMALL_DT_SCROLL_MEDIA_QUERY = rangeQuery(
  BREAKPOINTS.tabletMin,
  BREAKPOINTS.tabletMax,
);
// 1024-1279px: small DT, 2 cards per chevron page
const SMALL_DESKTOP_SCROLL_MEDIA_QUERY = rangeQuery(
  BREAKPOINTS.smallDesktopMin,
  BREAKPOINTS.smallDesktopMax,
);
// 1280-1439px: RTB/R2B desktop, 2 cards per chevron page
const LARGE_DESKTOP_SCROLL_MEDIA_QUERY = rangeQuery(
  BREAKPOINTS.largeDesktopMin,
  BREAKPOINTS.largeDesktopMax,
);
// 1440px+: large desktop, full-width no carousel
const LARGE_DESKTOP_ABOVE_SCROLL_MEDIA_QUERY = minQuery(
  BREAKPOINTS.xLargeDesktopMin,
);

const getMaxScrollLeft = (track: HTMLUListElement) =>
  Math.max(0, track.scrollWidth - track.clientWidth);

const clampScrollLeft = (track: HTMLUListElement, value: number) => {
  const maxScrollLeft = getMaxScrollLeft(track);
  return Math.min(Math.max(0, value), maxScrollLeft);
};

const COMPONENT_CLICK_NAMES: Record<string, string> = {
  clickandcollect: "C&C countdown timer",
  "free-returns": "Free returns",
  "price-promise": "Price promise / NKU",
  fashion: "Fashion styling appointments",
  guarantee: "2 / 5 year guarantee",
  home: "Home design appointments",
  nursery: "Nursery appointments",
  "my-jl-beauty": "MyJL Beauty loyalty",
  "protect-plus": "Protect plus",
  "installation-recycling": "Installation & Recycling",
  "next-day-delivery": "Next day delivery",
};

type Props = {
  cards: Card[];
};

const descriptionLabel = (card: Card) =>
  typeof card.description === "string" ? card.description : "";

export const ReasonToBuyUI = ({ cards }: Props) => {
  const trackRef = useRef<HTMLUListElement>(null);
  const inViewRef = useRef<HTMLSpanElement>(null);
  const lastScrollLeftRef = useRef(0);
  const lastTrackedScrollDirectionRef = useRef<number | null>(null);
  const scrollGestureTimeoutRef = useRef<number | null>(null);
  const [activePage, setActivePage] = useState(0);
  const activePageRef = useRef(0);
  const [cardsPerPage, setCardsPerPage] = useState(1);
  const [showChevronNavigation, setShowChevronNavigation] = useState(false);
  const isChevronBtnScrollRef = useRef(false);
  const scrollResetTimeoutRef = useRef<number | null>(null);
  const totalPages = Math.max(1, Math.ceil(cards.length / cardsPerPage));

  useR2BInView(inViewRef);

  useEffect(() => {
    activePageRef.current = activePage;
  }, [activePage]);

  useEffect(
    () => () => {
      if (scrollResetTimeoutRef.current !== null) {
        window.clearTimeout(scrollResetTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(function trackScrollDirectionEffect() {
    const track = trackRef.current;
    if (!track) return;

    const trackScrollDirection = (direction: number) => {
      if (direction === 0) return;

      if (direction !== lastTrackedScrollDirectionRef.current) {
        dispatchMonetateQEvent(
          direction > 0
            ? "WPDP | Reason to buy | Scroll right"
            : "WPDP | Reason to buy | Scroll left",
        );
        lastTrackedScrollDirectionRef.current = direction;
      }

      if (scrollGestureTimeoutRef.current !== null) {
        window.clearTimeout(scrollGestureTimeoutRef.current);
      }
      scrollGestureTimeoutRef.current = window.setTimeout(() => {
        lastTrackedScrollDirectionRef.current = null;
        scrollGestureTimeoutRef.current = null;
      }, 150);
    };

    lastScrollLeftRef.current = track.scrollLeft;
    const handleScrollDirection = () => {
      const currentScrollLeft = track.scrollLeft;
      const direction = Math.sign(
        currentScrollLeft - lastScrollLeftRef.current,
      );
      lastScrollLeftRef.current = currentScrollLeft;
      trackScrollDirection(direction);
    };

    track.addEventListener("scroll", handleScrollDirection, {
      passive: true,
    });
    return () => {
      track.removeEventListener("scroll", handleScrollDirection);
      if (scrollGestureTimeoutRef.current !== null) {
        window.clearTimeout(scrollGestureTimeoutRef.current);
      }
    };
  }, []);

  useEffect(
    function listenToMediaQueryChanges() {
      const mqSmallMobile = window.matchMedia(SMALL_MOBILE_SCROLL_MEDIA_QUERY);
      const mqLargeMobile = window.matchMedia(LARGE_MOBILE_SCROLL_MEDIA_QUERY);
      const mqVerySmallDt = window.matchMedia(VERY_SMALL_DT_SCROLL_MEDIA_QUERY);
      const mqSmallDesktop = window.matchMedia(
        SMALL_DESKTOP_SCROLL_MEDIA_QUERY,
      );
      const mqLargeDesktop = window.matchMedia(
        LARGE_DESKTOP_SCROLL_MEDIA_QUERY,
      );
      const mqLargeDesktopAbove = window.matchMedia(
        LARGE_DESKTOP_ABOVE_SCROLL_MEDIA_QUERY,
      );

      const update = () => {
        const matchMediaState: MatchMediaState = {
          smallMobile: mqSmallMobile.matches,
          largeMobile: mqLargeMobile.matches,
          verySmallDt: mqVerySmallDt.matches,
          smallDesktop: mqSmallDesktop.matches,
          largeDesktop: mqLargeDesktop.matches,
          largeDesktopAbove: mqLargeDesktopAbove.matches,
        };

        setShowChevronNavigation(shouldShowChevronNavigation(matchMediaState));
        setCardsPerPage(getCardsPerPage(cards.length, matchMediaState));
      };

      const mediaQueries = [
        mqSmallMobile,
        mqLargeMobile,
        mqVerySmallDt,
        mqSmallDesktop,
        mqLargeDesktop,
        mqLargeDesktopAbove,
      ];

      update();

      mediaQueries.forEach((mq) => {
        if (typeof mq.addEventListener === "function") {
          mq.addEventListener("change", update);
        } else if (typeof mq.addListener === "function") {
          mq.addListener(update);
        }
      });
      return () => {
        mediaQueries.forEach((mq) => {
          if (typeof mq.removeEventListener === "function") {
            mq.removeEventListener("change", update);
          } else if (typeof mq.removeListener === "function") {
            mq.removeListener(update);
          }
        });
      };
    },
    [cards.length],
  );

  useEffect(
    function syncActivePageWithScroll() {
      const track = trackRef.current;
      if (!track) return;

      const handleScroll = () => {
        // Skip recalculation while a chevron-triggered smooth scroll is in flight.
        if (isChevronBtnScrollRef.current) {
          return;
        }

        const items = Array.from(
          track.querySelectorAll<HTMLElement>("[data-card-index]"),
        );
        if (!items.length) return;

        const closestIndex = getClosestCardIndex(items, track.scrollLeft);
        const clampedIndex = Math.max(
          0,
          Math.min(closestIndex, items.length - 1),
        );
        const nextPage = Math.min(
          Math.max(Math.floor(clampedIndex / cardsPerPage), 0),
          totalPages - 1,
        );

        if (nextPage !== activePageRef.current) {
          activePageRef.current = nextPage;
          setActivePage(nextPage);
        }
      };

      // Sync on mount and whenever cardsPerPage changes
      handleScroll();

      track.addEventListener("scroll", handleScroll, { passive: true });
      return () => track.removeEventListener("scroll", handleScroll);
    },
    [cards.length, cardsPerPage, totalPages],
  );

  if (!cards.length) {
    return null;
  }

  const setActivePageState = (pageIndex: number) => {
    const safePageIndex = Math.max(0, Math.min(pageIndex, totalPages - 1));

    if (safePageIndex === activePageRef.current) {
      return safePageIndex;
    }

    activePageRef.current = safePageIndex;
    setActivePage(safePageIndex);
    return safePageIndex;
  };

  const scrollToPage = (pageIndex: number) => {
    const track = trackRef.current;
    if (!track) return;

    const items = Array.from(
      track.querySelectorAll<HTMLElement>("[data-card-index]"),
    );
    if (!items.length) return;

    const safePageIndex = Math.max(0, Math.min(pageIndex, totalPages - 1));
    const targetCardIndex = Math.min(
      safePageIndex * cardsPerPage,
      items.length - 1,
    );
    const targetCard = items[targetCardIndex];
    if (!targetCard) return;

    const left = clampScrollLeft(track, Math.max(0, targetCard.offsetLeft));

    // Mark this as a programmatic scroll so the scroll event handler doesn't recalculate activePage.
    isChevronBtnScrollRef.current = true;

    if (scrollResetTimeoutRef.current !== null) {
      window.clearTimeout(scrollResetTimeoutRef.current);
    }
    scrollResetTimeoutRef.current = window.setTimeout(() => {
      isChevronBtnScrollRef.current = false;
      scrollResetTimeoutRef.current = null;
    }, 250);

    if (typeof track.scrollTo === "function") {
      track.scrollTo({
        left,
        behavior: "smooth",
      });
      return;
    }

    track.scrollLeft = left;
  };

  const goToPage = (pageIndex: number) => {
    const safePageIndex = Math.max(0, Math.min(pageIndex, totalPages - 1));
    const nextPage = setActivePageState(safePageIndex);
    scrollToPage(nextPage);
  };

  const scrollTrackBy = (direction: -1 | 1) => {
    const track = trackRef.current;
    if (!track) return;

    const items = Array.from(
      track.querySelectorAll<HTMLElement>("[data-card-index]"),
    );
    if (!items.length) return;

    const closestIndex = getClosestCardIndex(items, track.scrollLeft);

    const nextCardIndex = Math.max(
      0,
      Math.min(closestIndex + direction, items.length - 1),
    );

    if (nextCardIndex === closestIndex) {
      return;
    }

    const nextCard = items[nextCardIndex];
    if (!nextCard) return;
    const scrollLeft = clampScrollLeft(track, Math.max(0, nextCard.offsetLeft));

    const newPage = Math.min(
      Math.max(Math.floor(nextCardIndex / cardsPerPage), 0),
      totalPages - 1,
    );

    setActivePageState(newPage);

    if (typeof track.scrollTo === "function") {
      track.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    } else {
      track.scrollLeft = scrollLeft;
    }
  };

  const handleTrackKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      // When chevron navigation is shown, use per-page scrolling like the chevron buttons
      if (showChevronNavigation) {
        goToPage(activePageRef.current + 1);
      } else {
        scrollTrackBy(1);
      }
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      // When chevron navigation is shown, use per-page scrolling like the chevron buttons
      if (showChevronNavigation) {
        goToPage(activePageRef.current - 1);
      } else {
        scrollTrackBy(-1);
      }
    }
  };

  const handleCardClick = (card: Card, index: number) => {
    const componentName = COMPONENT_CLICK_NAMES[card.id];
    if (componentName) {
      dispatchMonetateQEvent(
        `WPDP | Reason to buy | Click on ${componentName}`,
      );
    }
    if (index < 4) {
      dispatchMonetateQEvent(
        `WPDP | Reason to buy | Click on R2B position ${index + 1}`,
      );
    }
    card.onCardClick?.();
  };

  const currentPage = activePageRef.current;

  return (
    <div data-component="ReasonToBuy" className={styles.container}>
      <span
        ref={inViewRef}
        className={styles.inViewMarker}
        data-testid="reason-to-buy-in-view-marker"
        aria-hidden="true"
      />
      <ul
        className={styles.cards}
        data-testid="reason-to-buy-cards-track"
        ref={trackRef}
        aria-label="Reason to buy cards"
        tabIndex={0}
        onKeyDown={handleTrackKeyDown}
      >
        {cards.map((card, index) => {
          const isCardButton = !!card.onCardClick;
          const hasFinalLine =
            !!card.description ||
            !!card.onLinkClick ||
            (!!card.linkUrl && !!card.linkText);
          const cardLink = card.onLinkClick ? (
            <UnderlinedButton
              onClick={card.onLinkClick}
              className={styles.cardDescriptionButton}
              data-testid={`reason-to-buy-card-${card.id}-button`}
              labelText={card.linkText || descriptionLabel(card)}
            />
          ) : card.linkUrl && card.linkText ? (
            <UnderlinedButton
              tag={"a"}
              className={styles.cardLinkButton}
              href={card.linkUrl}
              data-testid={`reason-to-buy-card-${card.id}-link`}
              aria-label={card.linkAriaLabel}
              labelText={card.linkText || descriptionLabel(card)}
            />
          ) : null;

          return (
            <li
              key={card.id}
              data-card-index={index}
              className={cx(styles.card, {
                cardButton: isCardButton,
                [card.className || ""]: !!card.className,
              })}
              data-testid={`reason-to-buy-card-${card.id}`}
              onClick={() => handleCardClick(card, index)}
              onKeyDown={(event) => {
                if (!isCardButton) return;

                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleCardClick(card, index);
                }
              }}
              tabIndex={isCardButton ? 0 : undefined}
              aria-label={
                isCardButton ? card.screenReaderTitle || card.title : undefined
              }
              role={isCardButton ? "button" : undefined}
            >
              <div
                className={cx(styles.cardHeader, {
                  cardHeaderWithIcon: !!card.icon,
                })}
              >
                {card.icon && (
                  <span className={styles.cardIcon}>{card.icon}</span>
                )}
                {isCardButton && (
                  <ChevronRightOutlined
                    size="medium"
                    className={styles.cardArrow}
                  />
                )}
              </div>
              <h3
                className={cx(styles.cardTitle, {
                  cardTitleNoFinalLine: !hasFinalLine,
                })}
              >
                {card.title}
              </h3>
              {cardLink ? (
                <span className={styles.cardLinkContainer}>
                  {cardLink}
                  {card.linkChevron && (
                    <ChevronRightOutlined
                      size="small"
                      className={styles.cardLinkChevron}
                      aria-hidden="true"
                    />
                  )}
                </span>
              ) : card.description ? (
                <p className={styles.cardDescription}>{card.description}</p>
              ) : null}
            </li>
          );
        })}
      </ul>
      {totalPages > 1 && (
        <div
          className={styles.pagination}
          data-testid="reason-to-buy-pagination"
        >
          {showChevronNavigation ? (
            <>
              <button
                type="button"
                className={styles.paginationButton}
                onClick={() => goToPage(currentPage - 1)}
                aria-label="Previous cards"
                disabled={currentPage === 0}
              >
                <ChevronLeftOutlined size="small" />
              </button>
              <button
                type="button"
                className={styles.paginationButton}
                onClick={() => goToPage(currentPage + 1)}
                aria-label="Next cards"
                disabled={currentPage >= totalPages - 1}
              >
                <ChevronRightOutlined size="small" />
              </button>
            </>
          ) : (
            Array.from({ length: totalPages }).map((_, pageIndex) => (
              <span
                key={`pagination-dot-${pageIndex}`}
                className={styles.paginationDot}
                data-active={pageIndex === activePage}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};
